import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { AiContractAnswer, AiAnalysis } from "../api/aiApi";

/** What the assistant is about. Pages set it; only records the user opened are ever used. */
export interface AiAssistantContext {
  kind: "contract" | "obligation" | "receivable";
  id: string;
  /** Shown in the panel header, e.g. the contract, obligation or receivable number and title. */
  label: string;
}

export interface AiChatTurn {
  id: string;
  question: string;
  status: "pending" | "answered" | "error";
  answer?: AiAnalysis<AiContractAnswer>;
  error?: string;
}

/**
 * One conversation, kept until the reader deletes it.
 *
 * Threads belong to the record they were held about, so opening a contract
 * again brings back what was asked of it; the history list offers every
 * thread, newest first, the way a chat app does.
 */
export interface AiChatThread {
  id: string;
  contextId: string;
  contextKind: AiAssistantContext["kind"];
  contextLabel: string;
  createdAt: string;
  updatedAt: string;
  turns: AiChatTurn[];
}

interface AiAssistantState {
  open: boolean;
  context: AiAssistantContext | null;
  /** Every saved conversation, by thread id. */
  threads: Record<string, AiChatThread>;
  /** The thread each record is currently showing. */
  activeThreadByContext: Record<string, string>;
  /**
   * A question picked before any record was open ("Try asking"), offered once
   * the reader opens one. Never sent on its own, and not kept between visits.
   */
  pendingQuestion: string | null;

  toggle: () => void;
  setPendingQuestion: (question: string | null) => void;
  openFor: (context: AiAssistantContext) => void;
  close: () => void;
  setContext: (context: AiAssistantContext | null) => void;
  /** The thread to read or add to for this record, creating one if needed. */
  ensureThread: (context: AiAssistantContext) => string;
  /** Put this record's next question in a new conversation. */
  startThread: (context: AiAssistantContext) => string;
  selectThread: (threadId: string) => void;
  deleteThread: (threadId: string) => void;
  addTurn: (threadId: string, turn: AiChatTurn) => void;
  updateTurn: (threadId: string, id: string, patch: Partial<AiChatTurn>) => void;
}

/** Where conversations are kept between visits, and how many. */
const STORAGE_KEY = "lawsigna:ai-chat:v2";
/** Conversations remembered in all, most recently used first. */
const MAX_THREADS = 40;
/** Turns kept per conversation: enough to read back, small enough for local storage. */
const MAX_TURNS = 40;

const now = () => new Date().toISOString();

const newThread = (context: AiAssistantContext): AiChatThread => ({
  id:
    globalThis.crypto?.randomUUID?.() ??
    `thread-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  contextId: context.id,
  contextKind: context.kind,
  contextLabel: context.label,
  createdAt: now(),
  updatedAt: now(),
  turns: [],
});

/**
 * What is written to local storage.
 *
 * A question still in flight when the tab closed can never be answered, so it
 * is dropped rather than restored as a spinner that never stops. Empty and
 * oldest conversations are trimmed so a long history cannot fill the quota.
 */
function forStorage(threads: Record<string, AiChatThread>) {
  const kept = Object.values(threads)
    .map((thread) => ({
      ...thread,
      turns: thread.turns
        .filter((turn) => turn.status !== "pending")
        .slice(-MAX_TURNS),
    }))
    .filter((thread) => thread.turns.length > 0)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, MAX_THREADS);
  return Object.fromEntries(kept.map((thread) => [thread.id, thread]));
}

export const useAiAssistantStore = create<AiAssistantState>()(
  persist(
    (set, get) => ({
      open: false,
      context: null,
      threads: {},
      activeThreadByContext: {},
      pendingQuestion: null,

      toggle: () => set((s) => ({ open: !s.open })),
      setPendingQuestion: (question) => set({ pendingQuestion: question }),
      openFor: (context) => set({ open: true, context }),
      close: () => set({ open: false }),
      // Leaving a page clears its context, so the panel never keeps offering a
      // record the user has navigated away from.
      setContext: (context) =>
        set((s) =>
          context === null && s.context === null
            ? s
            : { context, open: context ? s.open : false },
        ),

      ensureThread: (context) => {
        const state = get();
        const active = state.activeThreadByContext[context.id];
        if (active && state.threads[active]) return active;
        // The newest conversation held about this record, if there is one.
        const existing = Object.values(state.threads)
          .filter((thread) => thread.contextId === context.id)
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
        const thread = existing ?? newThread(context);
        set((s) => ({
          threads: existing ? s.threads : { ...s.threads, [thread.id]: thread },
          activeThreadByContext: {
            ...s.activeThreadByContext,
            [context.id]: thread.id,
          },
        }));
        return thread.id;
      },

      startThread: (context) => {
        const thread = newThread(context);
        set((s) => ({
          threads: { ...s.threads, [thread.id]: thread },
          activeThreadByContext: {
            ...s.activeThreadByContext,
            [context.id]: thread.id,
          },
        }));
        return thread.id;
      },

      selectThread: (threadId) =>
        set((s) => {
          const thread = s.threads[threadId];
          if (!thread) return s;
          return {
            activeThreadByContext: {
              ...s.activeThreadByContext,
              [thread.contextId]: threadId,
            },
          };
        }),

      deleteThread: (threadId) =>
        set((s) => {
          const thread = s.threads[threadId];
          if (!thread) return s;
          const threads = { ...s.threads };
          delete threads[threadId];
          const active = { ...s.activeThreadByContext };
          if (active[thread.contextId] === threadId)
            delete active[thread.contextId];
          return { threads, activeThreadByContext: active };
        }),

      addTurn: (threadId, turn) =>
        set((s) => {
          const thread = s.threads[threadId];
          if (!thread) return s;
          return {
            threads: {
              ...s.threads,
              [threadId]: {
                ...thread,
                updatedAt: now(),
                turns: [...thread.turns, turn],
              },
            },
          };
        }),

      updateTurn: (threadId, id, patch) =>
        set((s) => {
          const thread = s.threads[threadId];
          if (!thread) return s;
          return {
            threads: {
              ...s.threads,
              [threadId]: {
                ...thread,
                updatedAt: now(),
                turns: thread.turns.map((t) =>
                  t.id === id ? { ...t, ...patch } : t,
                ),
              },
            },
          };
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // The panel opens closed on a fresh visit, and its context comes from
      // whichever record the reader opens; only the conversations are kept.
      partialize: (state) => ({ threads: forStorage(state.threads) }),
      merge: (persisted, current) => ({
        ...current,
        threads: forStorage(
          (persisted as { threads?: Record<string, AiChatThread> })?.threads ??
            {},
        ),
      }),
    },
  ),
);
